import { Alert, addToast } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackToListButton from '../../components/buttons/GoBackToListButton';
import { SectionTitle } from '../../components/text';
import BaseLoader from './contexts/BaseLoader';
import { CreateRenditionModal } from './parts/CreateRendition/CreateRenditionModal';
import Form from './parts/Form';
import FormView from './parts/FormView';
import { PaymentStatus } from './parts/PaymentStatus';
import StudentBox from './parts/StudentBox';
import Table from './parts/Table';
import { DegreeDocuments } from './parts/TitulationDocuments';
import { createRendition, deleteRendition, updateRendition } from './requests';
import {
  CreateRenditionParams,
  CreateRenditionWithRefresh,
  DegreeProcessRendition,
} from './types';
import { DeleteRenditionModal } from './parts/CreateRendition/DeleteRenditionModal';

const dictPrefix = 'studentsDegreeStudent';

export default function StudentsDegreeStudent() {
  const { t } = useTranslation();
  const [activityId, setActivityId] = useState<number | string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editRendition, setEditRendition] = useState<
    DegreeProcessRendition | undefined
  >(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deletingRendition, setDeletingRendition] = useState<boolean>(false);

  const { studyPlanEnrollmentId } = useParams<{
    studyPlanEnrollmentId: string;
  }>();

  const handleSaveRendition = useCallback(
    async ({
      searchParams,
      refresh = async () => {},
    }: CreateRenditionWithRefresh) => {
      const { data, error } = editRendition?.id
        ? await updateRendition(editRendition.id, searchParams)
        : await createRendition(activityId, searchParams);

      if (error) {
        addToast({
          text: t(`${dictPrefix}.tableRenditions.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      }
      if (data) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(
            `${dictPrefix}.tableRenditions.${
              editRendition?.id
                ? 'successfullyOnUpdate'
                : 'successfullyOnCreate'
            }`,
          ),
        });
        setShowModal(false);
        refresh();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, activityId, showModal],
  );

  const handleDeleteRendition = useCallback(
    async ({ refresh = async () => {} }) => {
      if (!editRendition?.id) return;
      setDeletingRendition(true);
      const { data, error } = await deleteRendition(editRendition?.id);

      if (error) {
        addToast({
          text: t(`${dictPrefix}.tableRenditions.deleteModal.failed`),
          color: 'danger',
          icon: 'error',
        });
      }

      if (data) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${dictPrefix}.tableRenditions.deleteModal.success`),
        });
        refresh();
      }
      setShowDeleteModal(false);
      setDeletingRendition(false);
    },
    [editRendition?.id, t],
  );

  const texts = useMemo(
    () => ({
      title: t(`${dictPrefix}.title`),
      subtitle: t(`${dictPrefix}.subtitle`),
      subtitleWitoutActivities: t(`${dictPrefix}.subtitleWitoutActivities`),
      box: t<
        string,
        Record<
          'student' | 'studyPlan' | 'schedule' | 'semester' | 'campus',
          string
        >
      >(`${dictPrefix}.box`, {
        returnObjects: true,
      }),
      noActivities: t(`${dictPrefix}.noActivities`),
      paymentStatus: {
        title: t(`${dictPrefix}.paymentStatus.title`),
        table: {
          noResults: t(`${dictPrefix}.paymentStatus.table.noResults`),
          headers: {
            concept: t(`${dictPrefix}.paymentStatus.table.headers.concept`),
            amount: t(`${dictPrefix}.paymentStatus.table.headers.amount`),
            paymentDate: t(
              `${dictPrefix}.paymentStatus.table.headers.paymentDate`,
            ),
            status: t(`${dictPrefix}.paymentStatus.table.headers.status`),
            action: t(`${dictPrefix}.paymentStatus.table.headers.action`),
          },
          values: {
            concept: t(`${dictPrefix}.paymentStatus.table.values.concept`),
            button: t(`${dictPrefix}.paymentStatus.table.values.button`),
            payment: {
              pending: t(
                `${dictPrefix}.paymentStatus.table.values.payment.pending`,
              ),
              paid: t(`${dictPrefix}.paymentStatus.table.values.payment.paid`),
            },
          },
        },
      },
      degreeDocuments: {
        title: t(`${dictPrefix}.degreeDocuments.title`),
        selectOptions: {
          rejected: t(`${dictPrefix}.degreeDocuments.selectOptions.rejected`),
          approved: t(`${dictPrefix}.degreeDocuments.selectOptions.approved`),
          pending: t(`${dictPrefix}.degreeDocuments.selectOptions.pending`),
        },
        toasts: {
          upload: {
            success: t(`${dictPrefix}.degreeDocuments.toasts.upload.success`),
            failed: t(`${dictPrefix}.degreeDocuments.toasts.upload.failed`),
          },
          remove: {
            success: t(`${dictPrefix}.degreeDocuments.toasts.remove.success`),
            failed: t(`${dictPrefix}.degreeDocuments.toasts.remove.failed`),
          },
          editStatus: {
            success: t(
              `${dictPrefix}.degreeDocuments.toasts.editStatus.success`,
            ),
            failed: t(`${dictPrefix}.degreeDocuments.toasts.editStatus.failed`),
          },
          confirmDelete: {
            title: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.title`,
            ),
            description: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.description`,
            ),
            confirm: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.confirm`,
            ),
            cancel: t(
              `${dictPrefix}.degreeDocuments.toasts.confirmDelete.cancel`,
            ),
          },
        },
        table: {
          noResults: t(`${dictPrefix}.degreeDocuments.table.noResults`),
          headers: {
            name: t(`${dictPrefix}.degreeDocuments.table.headers.name`),
            documentName: t(
              `${dictPrefix}.degreeDocuments.table.headers.documentName`,
            ),
            load: t(`${dictPrefix}.degreeDocuments.table.headers.load`),
            state: t(`${dictPrefix}.degreeDocuments.table.headers.state`),
          },
          values: {
            loadButton: t(
              `${dictPrefix}.degreeDocuments.table.values.loadButton`,
            ),
          },
        },
      },
    }),
    [t],
  );

  return (
    <BaseLoader studyPlanEnrollmentId={studyPlanEnrollmentId}>
      {({ data, refresh }) => {
        const hasPendingActivities =
          data.degreeProcess.activities.length > 0 &&
          data.degreeProcess.activities.some(
            (a) => a.state?.rendition.approved !== true,
          );
        const hasPendingPayments =
          data.paymentInfo && data.paymentInfo?.status !== 'paid';
        const hasPendingDocuments = false;
        const isDisabled =
          hasPendingActivities || hasPendingPayments || hasPendingDocuments;
        return (
          <Card className="p-4 mt-2 mx-3">
            <section className="petition-info mb-4">
              <Row>
                <Col xs={12} sm={3}>
                  <GoBackToListButton />
                </Col>
                <Col xs={12} sm={9}>
                  <StudentBox
                    studentName={`${data.student.name}, ${data.student.lastNames}`}
                    studyPlanName={data.studyPlan.name}
                    scheduleName={data.schedule.name}
                    campusName={data.campus.name}
                    texts={texts.box}
                  />
                </Col>
              </Row>
              <br className="mt-4" />
            </section>

            <section className="petition-summary mb-4">
              <div className="mb-3">
                <SectionTitle text={texts.title} />
              </div>
              <div className="mb-3">
                {Boolean(data.degreeProcess.activities.length) ? (
                  <p>{texts.subtitle}</p>
                ) : (
                  <p>{texts.subtitleWitoutActivities}</p>
                )}
              </div>
              {Boolean(data.degreeProcess.activities.length) && (
                <div className="mb-4">
                  <Table
                    data={data.degreeProcess.activities}
                    gradeAverage={data.degreeProcess.gradeAverage}
                    toggleModalRendition={(activityId, editRendition) => {
                      setActivityId(activityId);
                      setShowModal(!showModal);
                      setEditRendition(editRendition);
                    }}
                    toggleDeleteModal={(editRendition) => {
                      setEditRendition(editRendition);
                      setShowDeleteModal(!showDeleteModal);
                    }}
                    enrollmentStatus={data.studyPlanEnrollment.status}
                  />
                  <CreateRenditionModal
                    show={showModal}
                    toggleModal={() => {
                      setShowModal(!showModal);
                    }}
                    onSave={(searchParams: CreateRenditionParams) =>
                      handleSaveRendition({ searchParams, refresh })
                    }
                    activityId={activityId}
                    editRendition={editRendition}
                  />
                  {showDeleteModal && (
                    <DeleteRenditionModal
                      toggleModal={() => setShowDeleteModal(!showDeleteModal)}
                      deleteRendition={() => handleDeleteRendition({ refresh })}
                      loading={deletingRendition}
                    />
                  )}
                </div>
              )}
              <DegreeDocuments
                studyPlanEnrollmentId={data.studyPlanEnrollment.id}
                documents={data.degreeDocuments}
                texts={texts.degreeDocuments}
                refresh={refresh}
              />
              {!data.degreeProcess.activities.length && (
                <div className="mb-4">
                  <Alert
                    className=""
                    color="info"
                    icon="information"
                    size="lg"
                    text={texts.noActivities}
                  />
                </div>
              )}
              {!!data.paymentInfo && (
                <PaymentStatus
                  data={data.paymentInfo}
                  texts={texts.paymentStatus}
                />
              )}
              {data.degreeProcess.summary ? (
                <FormView
                  date={data.degreeProcess.summary.date}
                  createdAt={data.degreeProcess.summary.createdAt}
                  graduationGrade={data.degreeProcess.summary.gradeGraduation}
                  degreeGrade={data.degreeProcess.summary.gradeDegreeProcess}
                  registerCode={data.degreeProcess.summary.registerCode}
                  certificateNumber={
                    data.degreeProcess.summary.certificateNumber
                  }
                  registryBook={data.degreeProcess.summary.registryBook}
                  distinctionRank={
                    data.degreeProcess.summary.distinctionRank?.name
                  }
                  exemptionCertificateCode={
                    data.degreeProcess.summary.exemptionCertificateCode
                  }
                />
              ) : (
                <Form
                  studyPlanEnrollmentId={studyPlanEnrollmentId}
                  degreeFinalNote={data.degreeFinalNote}
                  graduationGrade={data.graduation.grade}
                  distinctionRanks={data.distinctionRanks}
                  financialBlockade={data.financialBlockade}
                  registerCode={data.registerCode}
                  certificateNumber={data.certificateNumber}
                  registryBook={data.registryBook}
                  documents={data.degreeDocuments}
                  onComplete={() => refresh()}
                  disabled={isDisabled}
                  exemptionCertificateCode={data.exemptionCertificateCode}
                />
              )}
            </section>
          </Card>
        );
      }}
    </BaseLoader>
  );
}
